import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import restClient from 'Shared/hooks/restClient'
import Template from './Template'
import FileUploader from 'Shared/components/ImageUploader/FileUploader'
import AsyncSelect from 'Shared/components/AsyncSelect'
import './PageContainer.scoped.scss'
import useAct from '../useAct'

export default function PageContainer({ section }) {
  const act = useAct()

  const [wizard, setWizard] = useState(null)
  const templates = useSel((s) => s.templates.filter(t => t.section === section))

  useEffect(() => {
    act.templates.load()
  }, [])

  const setTemplate = (template) => {
    setWizard({
      flowTemplateId: template.id,
      fields: template.fields.filter(f => f.showInWizard).map(f => ({ ...f, placeholder: f.defaultValue }))
    })
  }

  return (
    <div>
      {!wizard && <ChooseFlowType section={section} templates={templates} setTemplate={setTemplate} />}
      {wizard && <WizardForm section={section} wizard={wizard} key={wizard.slug} setWizard={setWizard} />}
    </div>
  )
}

function ChooseFlowType({ section, templates, setTemplate }) {
  const backUrl = section === 'ballots' ? '/admin/ballots' : '/admin/flows'

  return <>
    <div className="header margin-left">
      <button className="button naked margin-bottom" onClick={() => window.location.href = backUrl}><SvgIconsBack /></button>
      <h1 className="margin-bottom">Create a { section === 'ballots' ? 'Ballot' : 'Page' }</h1>
    </div>
    <Template section={section} templates={templates} setTemplate={setTemplate} />
  </>
}

function WizardForm({ section, wizard, setWizard }) {
  const initialValues = wizard.fields.reduce((acc, field) => {
    if (field.questionType === 'checkbox') {
      if (field.options) {
        const defaultOption = field.options.find(option => option.default)
        acc[field.slug] = !!defaultOption
      } else {
        acc[field.slug] = false
      }
    } else if (field.questionType === 'select' && field.options) {
      acc[field.slug] = field.options[0].value
    } else {
      acc[field.slug] = ''
    }
    return acc
  }, {})

  const hasNameField = _.includes(wizard.fields.map(f => f.slug), 'name')

  const validationSchema = Yup.object().shape(
    wizard.fields.reduce((acc, field) => {
      let validationRule = Yup.string()

      if (field.questionType !== 'checkbox' && field.required !== false) {
        validationRule = validationRule.required('This field is required')
      }

      if (field.questionType === 'url') {
        validationRule = validationRule.matches(
          /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/i,
          'Please enter a valid URL'
        )
      }

      if (field.numberOnly) {
        validationRule = validationRule.matches(
          /^\d*$/,
          'Please enter numbers only'
        )
      }

      acc[field.slug] = validationRule
      return acc
    }, {})
  )

  const handleSubmit = (values, { setSubmitting }) => {
    restClient.post('/api/flows', {
      flowTemplateId: wizard.flowTemplateId,
      createFields: hasNameField ? values : _.omit(values, 'name'),
      name: values.name,
    })
      .then(({ data }) => {
        const flowId = data.id
        window.location.href = `/admin/flows/${flowId}/build?nav=preview`
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const disabled = ({ values }) => !_.every(wizard.fields, field => {
    // If field is not required, always return true
    if (field.required === false) return true;

    // For checkbox fields, an empty string is a valid state
    if (field.questionType === 'checkbox') {
      // values[field.slug] will be either the selected value or an empty string
      return true;
    }

    // For all other field types, check for truthy values
    return !!values[field.slug];
  })

  const entityName = section === 'ballots' ? 'Ballot' : 'Page'

  return (
    <div>
      <div className="header margin-left">
        <button className="button naked margin-bottom" onClick={() => setWizard(null)}><SvgIconsBack /></button>
        <h1 className="margin-bottom">{wizard.name}</h1>
      </div>
      <div className="row">
        <div className="box form">
          <h2>{entityName} details</h2>
          <p className="margin-bottom double">Add the following details to get started</p>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values }) => (
              <Form>
                {!hasNameField && <div className="field shorten" key={'name'}>
                  <label>Name</label>
                  <div className="url-box">
                    <Field
                      type="text"
                      name="name"
                      placeholder={`Name for your ${entityName.toLowerCase()}`}
                    />
                  </div>
                </div>}
                {wizard.fields.map(field => (
                  <div className="field shorten" key={field.slug}>
                    <label>{field.label}{field.required === false || field.questionType === 'checkbox' ? '' : ' *'}</label>
                    <div className="url-box">
                      {field.preText && <p>{field.preText}</p>}
                      {(field.questionType === 'text' || field.questionType === 'url') && (
                        <Field
                          type="text"
                          name={field.slug}
                          placeholder={field.placeholder}
                        />
                      )}
                    </div>
                    {field.questionType === 'paragraph' && (
                      <Field
                        as="textarea"
                        name={field.slug}
                        placeholder={field.placeholder}
                      />
                    )}
                    {field.questionType === 'select' && (
                      <Field as="select" name={field.slug}>
                        <option value="" disabled>{field.placeholder || "Please select an option"}</option>
                        {field.options.map((option, index) => (
                          <option key={index} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field>
                    )}
                    {field.questionType === 'async_select' && (
                      <Field name={field.slug}>
                        {({ field: formikField, form, meta }) => (
                          <AsyncSelect
                            entity={field.asyncEntity}
                            value={formikField.value}
                            onChange={(value) => form.setFieldValue(formikField.name, value)}
                            onBlur={() => form.setFieldTouched(formikField.name, true)}
                          />
                        )}
                      </Field>
                    )}
                    {field.questionType === 'checkbox' && (
                      <div className="checkbox">
                        <Field name={field.slug}>
                          {({ field: formField, form }) => (
                            <input
                              type="checkbox"
                              id={field.slug}
                              checked={formField.value === true}
                              onChange={e => {
                                form.setFieldValue(
                                  field.slug,
                                  e.target.checked ? true : false
                                )
                              }}
                            />
                          )}
                        </Field>
                        <label htmlFor={field.slug}>{field.label}</label>
                      </div>
                    )}
                    {field.questionType === 'image' &&
                      <Field name={field.slug}>
                        {({ field: formField, form }) => (
                          <div className="flex-center gap-10">
                            <FileUploader onUploaded={(blobIds) => form.setFieldValue(field.slug, blobIds[0])} />
                            {!!formField.value && <img src={`/rails/active_storage/blobs/${formField.value}/image.png`} width={150} />}
                          </div>
                        )}
                      </Field>
                    }
                    {field.description && (
                      <p className="text-small">{field.description}</p>
                    )}
                    <ErrorMessage
                      name={field.slug}
                      component={({ children }) => (
                        <p className="smaller error no-margin-bottom no-margin-top">{children}</p>
                      )}
                    />
                  </div>
                ))}
                <div className="field">
                  <button
                    type="submit"
                    className="button primary"
                    disabled={isSubmitting || disabled({ values })}
                  >
                    Create {entityName}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}
